import { Dialog } from '@headlessui/react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import { useI18n } from '../hooks/useI18n.ts';
import { importCSVFile } from '../services/csvImport';
import { Budget } from '../types/budget.ts';

interface ImportCSVButtonProps {
  showText?: boolean;
  className?: string;
  onImport: (budget: Budget) => void;
}

export const ImportCSVButton: React.FC<ImportCSVButtonProps> = ({
  showText = false,
  className = '',
  onImport,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const { defaults } = useI18n();

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const budget = await importCSVFile(file, defaults);

      // Show success toast notification
      showToast(t('budget.importSuccess'), 'success');

      onImport(budget);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import CSV file');
      setIsErrorDialogOpen(true);

      // Show error toast notification
      showToast(t('budget.importError'), 'error');
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const baseClasses =
    'flex cursor-pointer items-center rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-surface-600 dark:bg-surface-800 dark:text-slate-300 dark:hover:bg-surface-700';
  const labelClasses = className ? `${baseClasses} ${className}` : baseClasses;
  const inputId = `csv-upload-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <>
      <div className="flex items-center">
        <input
          type="file"
          accept=".csv"
          onChange={handleImport}
          ref={fileInputRef}
          className="hidden"
          id={inputId}
        />
        <label
          htmlFor={inputId}
          data-attr="import-csv-button"
          className={labelClasses}
          title={t('settings.exportImport.import')}
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          {showText && <span className="ml-2">{t('settings.exportImport.import')}</span>}
        </label>
      </div>

      {/* Error Dialog */}
      <Dialog
        open={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50" />

          <div className="relative mx-auto w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-surface-800">
            <div className="p-6">
              <Dialog.Title className="mb-4 text-lg font-medium text-slate-900 dark:text-white">
                {t('errors.generic')}
              </Dialog.Title>

              <div className="mt-2 max-h-[60vh] overflow-y-auto">
                <div className="rounded-md bg-slate-50 p-4 dark:bg-surface-800/50">
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-700 dark:text-slate-300">
                    {error}
                  </p>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('common.note')}:
                  </h4>
                  <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-3 text-xs text-slate-600 dark:bg-surface-700 dark:text-slate-300">
                    category,responsible,description,account,amount
                  </pre>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {t('common.note')}:
                    <br />
                    Salaire,Jérôme,"Salaire Janvier",Courant Jérôme,2500
                    <br />
                    Courses,Jess,"Courses Carrefour",Courant Jess,-150
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsErrorDialogOpen(false)}
                  className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
